import axios from 'axios';

// Helper function to get API URL - uses relative path in production, full URL in preview
const getApiUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // If we're on the production domain, use relative URLs
    if (hostname.includes('justasmile-events.com') || hostname.includes('app.justasmile')) {
      return '';  // Relative path - will use same domain
    }
  }
  // Otherwise use the configured backend URL (for preview/development)
  return process.env.REACT_APP_BACKEND_URL || '';
};

const API_URL = getApiUrl();

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors - only redirect if not already on login page
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only clear token and redirect if we're not already on the login page
      const isLoginPage = window.location.pathname.includes('/login');
      const isPublicRoute = window.location.pathname.startsWith('/api/public') || 
                           !window.location.pathname.includes('/dashboard');
      
      if (!isLoginPage && !isPublicRoute) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Settings
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// WooCommerce Products
export const woocommerceAPI = {
  testConnection: () => api.get('/woocommerce/test'),
  getShopInfo: () => api.get('/woocommerce/shop-info'),
  getProducts: () => api.get('/woocommerce/products'),
  getProduct: (id) => api.get(`/woocommerce/products/${id}`),
  updateRentalSettings: (productId, data) => api.put(`/woocommerce/products/${productId}/rental-settings`, data),
};

// Reservations
export const reservationsAPI = {
  getAll: (status) => api.get('/reservations', { params: { status } }),
  get: (id) => api.get(`/reservations/${id}`),
  create: (data) => api.post('/reservations', data),
  updateStatus: (id, status) => api.put(`/reservations/${id}/status`, { status }),
  markPayment: (id, method) => api.put(`/reservations/${id}/payment`, null, { params: { payment_method: method } }),
  exportPDF: (id) => api.get(`/reservations/${id}/export/pdf`, { responseType: 'blob' }),
  exportExcel: (id) => api.get(`/reservations/${id}/export/excel`, { responseType: 'blob' }),
};

// Planning
export const planningAPI = {
  get: () => api.get('/planning'),
};

// Callbacks
export const callbacksAPI = {
  getAll: (status) => api.get('/callbacks', { params: { status } }),
  updateStatus: (id, status) => api.put(`/callbacks/${id}/status`, null, { params: { status } }),
};

// Links
export const linksAPI = {
  getAll: () => api.get('/links'),
  create: (data) => api.post('/links', data),
  delete: (id) => api.delete(`/links/${id}`),
  getStats: (id) => api.get(`/links/${id}/stats`),
};

// Traffic
export const trafficAPI = {
  getStats: (days = 30) => api.get('/traffic/stats', { params: { days } }),
};

// Dashboard
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Super Admin
export const superAdminAPI = {
  getStats: () => api.get('/super-admin/stats'),
  getClients: (params) => api.get('/super-admin/clients', { params }),
  getClient: (id) => api.get(`/super-admin/clients/${id}`),
  deleteClient: (id) => api.delete(`/super-admin/clients/${id}`),
  updateClientSubscription: (id, modules) => api.put(`/super-admin/clients/${id}/subscription`, modules),
  impersonate: (id) => api.post(`/super-admin/impersonate/${id}`),
  stopImpersonation: () => api.post('/super-admin/stop-impersonation'),
  // Modules & Packs
  getModules: () => api.get('/admin/modules'),
  createModule: (data) => api.post('/admin/modules', data),
  updateModule: (id, data) => api.put(`/admin/modules/${id}`, data),
  deleteModule: (id) => api.delete(`/admin/modules/${id}`),
  getPacks: () => api.get('/admin/packs'),
  createPack: (data) => api.post('/admin/packs', data),
  updatePack: (id, data) => api.put(`/admin/packs/${id}`, data),
  getPromoCodes: () => api.get('/admin/promo-codes'),
  createPromoCode: (data) => api.post('/admin/promo-codes', data),
  // Site templates
  getTemplates: () => api.get('/super-admin/templates'),
  createTemplate: (data) => api.post('/super-admin/templates', data),
  updateTemplate: (id, data) => api.put(`/super-admin/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/super-admin/templates/${id}`),
  generateTemplateAI: (data) => api.post('/super-admin/templates/generate-ai', data),
  scrapeTemplate: (data) => api.post('/super-admin/templates/scrape-site', data),
  createClientSite: (clientId, data) => api.post(`/super-admin/clients/${clientId}/site`, data),
};

// Site Builder (for clients)
export const siteBuilderAPI = {
  getMySite: (siteId) => api.get('/my-site', { params: siteId ? { site_id: siteId } : {} }),
  getMySites: () => api.get('/my-sites'),
  createSite: (data) => api.post('/sites/create', data),
  deleteSite: (siteId) => api.delete(`/sites/${siteId}`),
  updateSite: (data) => api.put('/my-site', data),
  updateSiteData: (data) => api.put('/my-site/data', data),
  updatePage: (data) => api.put('/my-site/page', data),
  autoSavePage: (data) => api.put('/my-site/page/auto-save', data),
  updatePageSEO: (pageSlug, seo) => api.put(`/my-site/page/${pageSlug}/seo`, seo),
  getPageHistory: (pageSlug) => api.get(`/my-site/page/${pageSlug}/history`),
  restorePageVersion: (pageSlug, versionId) => api.post(`/my-site/page/${pageSlug}/restore/${versionId}`),
  createPage: (data) => api.post('/my-site/pages', data),
  deletePage: (pageSlug) => api.delete(`/my-site/pages/${pageSlug}`),
  updateStyles: (styles) => api.put('/my-site/styles', styles),
  publishSite: () => api.put('/my-site/publish'),
  unpublishSite: () => api.put('/my-site/unpublish'),
  analyzeSEO: (pageSlug) => api.post(`/my-site/page/${pageSlug}/analyze-seo`),
  generateSEOPages: (data) => api.post('/my-site/generate-seo-pages', data),
  updateConfig: (config) => api.put('/my-site/config', config),
  duplicateSite: (data) => api.post('/my-site/duplicate', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deploySite: (data) => api.post('/my-site/deploy', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  analyzeKeywords: (data) => api.post('/my-site/seo/analyze-keywords', data),
  generateSEOPagesFromAnalysis: (pages) => api.post('/my-site/seo/generate-pages', pages),
  // Analytics - track page views
  trackPageView: (data) => api.post('/analytics/track', data),
  // Social & Reviews
  updateSocialConfig: (config) => api.put('/my-site/social-config', config),
  getSocialFeeds: () => api.get('/my-site/social-feeds'),
  syncInstagram: () => api.post('/my-site/sync-instagram'),
  configureGoogleReviews: (config) => api.post('/my-site/google-reviews/config', config),
  syncGoogleReviews: () => api.post('/my-site/google-reviews/sync'),
  getGoogleReviews: () => api.get('/my-site/google-reviews'),
};

// Client Portal (Mon Compte)
export const clientPortalAPI = {
  // Dashboard
  getDashboard: () => api.get('/client-portal/dashboard'),
  
  // Reservations
  getReservations: (status) => api.get('/client-portal/reservations', { params: { status } }),
  getReservation: (id) => api.get(`/client-portal/reservations/${id}`),
  
  // Invoices
  getInvoices: (status) => api.get('/client-portal/invoices', { params: { status } }),
  getInvoice: (id) => api.get(`/client-portal/invoices/${id}`),
  
  // Contracts
  getContracts: (status) => api.get('/client-portal/contracts', { params: { status } }),
  getContract: (id) => api.get(`/client-portal/contracts/${id}`),
  signContract: (id, signatureData) => api.post(`/client-portal/contracts/${id}/sign`, signatureData),
};

// Media Library
export const mediaAPI = {
  upload: (file, category = 'general', optimize = true) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    formData.append('optimize', optimize);
    return api.post('/media/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getAll: (category) => api.get('/media', { params: { category } }),
  update: (mediaId, data) => api.put(`/media/${mediaId}`, data),
  delete: (mediaId) => api.delete(`/media/${mediaId}`),
  getCategories: () => api.get('/media/categories'),
  createCategory: (data) => api.post('/media/category', data),
  optimizeSeo: (mediaId) => api.post(`/media/${mediaId}/optimize-seo`),
};

// Analytics & Traffic Tracking
export const analyticsAPI = {
  trackPageView: (data) => api.post('/analytics/track', data),
  getRealtimeStats: (siteId) => api.get(`/analytics/realtime/${siteId}`),
  getTrafficStats: (siteId, period = '7d') => api.get(`/analytics/stats/${siteId}`, { params: { period } }),
};

// SEO Assistant (AI-powered)
export const seoAssistantAPI = {
  analyzePage: (data) => api.post('/seo-assistant/analyze-page', data),
  getSuggestions: (pageSlug, status) => api.get(`/seo-assistant/suggestions/${pageSlug}`, { params: { status } }),
  approveSuggestion: (suggestionId) => api.put(`/seo-assistant/suggestions/${suggestionId}/approve`),
  rejectSuggestion: (suggestionId) => api.put(`/seo-assistant/suggestions/${suggestionId}/reject`),
  generateSEOPage: (data) => api.post('/seo-assistant/generate-seo-page', data),
  generateBulkSEOPages: (keyword, cities) => api.post('/seo-assistant/generate-bulk-seo-pages', { keyword, cities }),
  optimizeContent: (content, targetKeyword) => api.post('/seo-assistant/optimize-content', { content, target_keyword: targetKeyword }),
};

// MyPOS Payments
export const myposAPI = {
  getConfig: () => api.get('/payments/mypos/config'),
  saveConfig: (config) => api.post('/payments/mypos/config', config),
  createPayment: (data) => api.post('/payments/mypos/create-payment', data),
  getPaymentStatus: (paymentId) => api.get(`/payments/mypos/payment/${paymentId}`),
  listPayments: (status, limit = 50) => api.get('/payments/mypos/payments', { params: { status, limit } }),
  simulateSuccess: (paymentId) => api.post(`/payments/mypos/simulate-success/${paymentId}`),
  refundPayment: (paymentId, amount) => api.post(`/payments/mypos/refund/${paymentId}`, { amount }),
};

// Stripe Payments
export const stripeAPI = {
  getConfig: () => api.get('/payments/stripe/config'),
  createCheckout: (reservationId, paymentType = 'deposit') => 
    api.post('/payments/stripe/create-checkout', {
      reservation_id: reservationId,
      payment_type: paymentType,
      origin_url: window.location.origin
    }),
  getPaymentStatus: (sessionId) => api.get(`/payments/stripe/status/${sessionId}`),
  getTransactions: (reservationId, status, limit = 50) => 
    api.get('/payments/stripe/transactions', { params: { reservation_id: reservationId, status, limit } }),
  getTransaction: (transactionId) => api.get(`/payments/stripe/transaction/${transactionId}`),
};

// Geolocation & Delivery
export const geoAPI = {
  autocomplete: (query, country = 'fr') => api.get('/geo/autocomplete', { params: { query, country } }),
  getPlaceDetails: (placeId) => api.get(`/geo/place-details/${placeId}`),
  calculateDistance: (origin, destination) => api.post('/geo/calculate-distance', null, { params: { origin, destination } }),
  estimateDeliveryFee: (postalCode, originPostal = '13800') => api.get('/geo/delivery-estimate', { params: { postal_code: postalCode, origin_postal: originPostal } }),
};

// Forms
export const formsAPI = {
  create: (data) => api.post('/forms', data),
  getAll: () => api.get('/forms'),
  get: (formId) => api.get(`/forms/${formId}`),
  update: (formId, data) => api.put(`/forms/${formId}`, data),
  delete: (formId) => api.delete(`/forms/${formId}`),
  getSubmissions: (formId) => api.get(`/forms/${formId}/submissions`),
};

// Customers (CRM)
export const customersAPI = {
  create: (data) => api.post('/customers', data),
  getAll: (params) => api.get('/customers', { params }),
  get: (customerId) => api.get(`/customers/${customerId}`),
  update: (customerId, data) => api.put(`/customers/${customerId}`, data),
  delete: (customerId) => api.delete(`/customers/${customerId}`),
  import: (customers) => api.post('/customers/import', customers),
};

// Campaigns (SMS & Email)
export const campaignsAPI = {
  createTemplate: (data) => api.post('/campaigns/templates', data),
  getTemplates: (type) => api.get('/campaigns/templates', { params: { type } }),
  create: (data) => api.post('/campaigns', data),
  getAll: () => api.get('/campaigns'),
  get: (campaignId) => api.get(`/campaigns/${campaignId}`),
};

// Loyalty Program
export const loyaltyAPI = {
  getConfig: () => api.get('/settings/loyalty'),
  updateConfig: (config) => api.put('/settings/loyalty', config),
  addTransaction: (data) => api.post('/loyalty/transaction', data),
  getCustomerLoyalty: (customerId) => api.get(`/loyalty/customer/${customerId}`),
};

// Payment Methods
export const paymentAPI = {
  getConfig: () => api.get('/settings/payment-methods'),
  updateConfig: (config) => api.put('/settings/payment-methods', config),
};

// Distance
export const distanceAPI = {
  calculate: (origin, destination) => api.post('/distance/calculate', { origin, destination }),
};

// Public APIs (for widgets)
export const publicAPI = {
  getWCProducts: (userId) => axios.get(`${API_URL}/api/public/woocommerce/${userId}/products`),
  getWCProduct: (userId, productId, variationId) => axios.get(`${API_URL}/api/public/woocommerce/${userId}/products/${productId}`, { params: { variation_id: variationId } }),
  createReservation: (data) => axios.post(`${API_URL}/api/reservations`, data),
  createCallback: (data) => axios.post(`${API_URL}/api/public/callback`, data),
  trackLink: (code) => axios.get(`${API_URL}/api/public/track/${code}`),
  recordVisit: (data) => axios.post(`${API_URL}/api/public/visit`, data),
  getTemplates: () => axios.get(`${API_URL}/api/public/templates`),
};

export default api;
